/**
 * DailyTrendChart — Area/line chart showing rolling daily sales trend.
 * Displays Daily Sales, Daily Budget, and Prior Year Daily Sales
 * for all available days in the dataset.
 *
 * Uses IQR-based outlier detection to clamp the Y-axis domain so that
 * extreme spikes (e.g., month-end adjustments) don't crush the visible
 * daily variation. Outlier values are still shown in the tooltip.
 */

import { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import { DailyTrendPoint } from "../utils/PowerBIApiCall";
import {
  formatCurrencyAxis as formatYAxis,
  formatCurrency as formatTooltipValue,
} from "../utils/format";
import { SERIES, GRID, AXIS_TICK } from "../utils/chartTheme";

interface DailyTrendChartProps {
  data: DailyTrendPoint[];
}

// ─── Outlier helpers ────────────────────────────────────────────────────────

/** IQR multiplier — 1.5 is standard Tukey fence, 2.0 is slightly looser */
const IQR_K = 2.0;

/**
 * Compute a Y-axis domain that excludes extreme outliers.
 * Uses the IQR method on ALL numeric series (dailySales, dailyBudget, pyDailySales)
 * to find robust lower/upper fences for the axis.
 */
function computeRobustDomain(data: DailyTrendPoint[]): [number, number] {
  // Collect all values across series
  const allValues: number[] = [];
  for (const d of data) {
    if (d.dailySales != null) allValues.push(d.dailySales);
    if (d.dailyBudget != null) allValues.push(d.dailyBudget);
    if (d.pyDailySales != null) allValues.push(d.pyDailySales);
  }

  if (allValues.length < 4) {
    // Not enough data for IQR — fall back to simple min/max
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const pad = (max - min) * 0.1 || 1;
    return [min - pad, max + pad];
  }

  const sorted = [...allValues].sort((a, b) => a - b);
  const q1 = sorted[Math.floor(sorted.length * 0.25)];
  const q3 = sorted[Math.floor(sorted.length * 0.75)];
  const iqr = q3 - q1;

  const lowerFence = q1 - IQR_K * iqr;
  const upperFence = q3 + IQR_K * iqr;

  // Use the actual range of non-outlier values (not the raw IQR fence)
  // so the domain tightly fits the meaningful data, not the fence formula.
  const nonOutlierValues = allValues.filter(
    (v) => v >= lowerFence && v <= upperFence,
  );
  const actualMin =
    nonOutlierValues.length > 0 ? Math.min(...nonOutlierValues) : 0;
  const domainMin = Math.min(0, actualMin);
  const domainMax = upperFence;
  const padding = (domainMax - domainMin) * 0.08;

  return [Math.floor(domainMin - padding), Math.ceil(domainMax + padding)];
}

/**
 * Clamp chart data values to the robust domain so the line stays visible.
 * Original values are preserved in `_original*` fields for accurate tooltips.
 */
interface ClampedTrendPoint extends DailyTrendPoint {
  _originalDailySales?: number;
  _originalPyDailySales?: number;
}

function clampData(
  data: DailyTrendPoint[],
  domain: [number, number],
): ClampedTrendPoint[] {
  const [lo, hi] = domain;
  return data.map((d) => {
    const clamped: ClampedTrendPoint = { ...d };
    if (d.dailySales < lo || d.dailySales > hi) {
      clamped._originalDailySales = d.dailySales;
      clamped.dailySales = Math.max(lo, Math.min(hi, d.dailySales));
    }
    if (d.pyDailySales < lo || d.pyDailySales > hi) {
      clamped._originalPyDailySales = d.pyDailySales;
      clamped.pyDailySales = Math.max(lo, Math.min(hi, d.pyDailySales));
    }
    return clamped;
  });
}

// ─── Custom Tooltip ─────────────────────────────────────────────────────────

interface TooltipPayloadEntry {
  color: string;
  name: string;
  value: number;
  dataKey: string;
  payload: ClampedTrendPoint;
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayloadEntry[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;

  const row = payload[0]?.payload;

  return (
    <div className="bg-white border border-gray-200/70 rounded-xl shadow-pop p-3">
      <p className="text-xs font-semibold text-gray-700 mb-1.5">{label}</p>
      {payload.map((entry) => {
        // Show original (unclamped) value if it was an outlier
        let displayValue = entry.value;
        if (
          entry.dataKey === "dailySales" &&
          row?._originalDailySales != null
        ) {
          displayValue = row._originalDailySales;
        }
        if (
          entry.dataKey === "pyDailySales" &&
          row?._originalPyDailySales != null
        ) {
          displayValue = row._originalPyDailySales;
        }

        return (
          <p
            key={entry.name}
            className="text-xs"
            style={{ color: entry.color }}
          >
            <span className="font-medium">{entry.name}:</span>{" "}
            <span className="tabular-nums">
              {formatTooltipValue(displayValue)}
            </span>
            {/* Show outlier indicator */}
            {((entry.dataKey === "dailySales" &&
              row?._originalDailySales != null) ||
              (entry.dataKey === "pyDailySales" &&
                row?._originalPyDailySales != null)) && (
              <span
                className="text-gray-400 ml-1"
                title="Outlier value — clamped on chart"
              >
                *
              </span>
            )}
          </p>
        );
      })}
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export function DailyTrendChart({ data }: DailyTrendChartProps) {
  if (!data || data.length === 0) {
    return null;
  }

  // Compute robust Y-axis domain and clamp outlier values for display
  const { chartData, domain, avgSales, outlierCount } = useMemo(() => {
    const dom = computeRobustDomain(data);
    const clamped = clampData(data, dom);
    const outliers = clamped.filter(
      (d) => d._originalDailySales != null || d._originalPyDailySales != null,
    ).length;

    // Average using original (unclamped) daily sales
    const avg = data.reduce((sum, d) => sum + d.dailySales, 0) / data.length;
    // Clamp the average to domain so the reference line is visible
    const clampedAvg = Math.max(dom[0], Math.min(dom[1], avg));

    return {
      chartData: clamped,
      domain: dom,
      avgSales: clampedAvg,
      outlierCount: outliers,
    };
  }, [data]);

  return (
    <div className="card overflow-hidden mb-6">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900">
          Daily Sales Trend
        </h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Rolling {data.length}-day view
          {outlierCount > 0 && (
            <span className="text-gray-400 ml-1">
              · {outlierCount} outlier{outlierCount > 1 ? "s" : ""} clamped
            </span>
          )}
        </p>
      </div>
      <div className="px-4 py-5">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart
            data={chartData}
            margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
          >
            <defs>
              <linearGradient id="gradientSales" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={SERIES.primary}
                  stopOpacity={0.3}
                />
                <stop offset="95%" stopColor={SERIES.primary} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid {...GRID} />
            <XAxis
              dataKey="dateFormatted"
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
            />
            <YAxis
              domain={domain}
              allowDataOverflow={true}
              tickFormatter={formatYAxis}
              tick={AXIS_TICK}
              tickLine={false}
              axisLine={false}
              width={65}
            />
            <Tooltip content={<CustomTooltip />} />
            {/* Average reference line */}
            <ReferenceLine
              y={avgSales}
              stroke={SERIES.priorYearLine}
              strokeDasharray="4 4"
              strokeWidth={1}
              label={{
                value: `Avg: ${formatYAxis(avgSales)}`,
                position: "right",
                fontSize: 10,
                fill: SERIES.priorYearLine,
              }}
            />
            <Legend iconType="line" wrapperStyle={{ fontSize: "12px" }} />
            {/* Daily Sales — primary area with gradient fill */}
            <Area
              type="monotone"
              dataKey="dailySales"
              name="Daily Sales"
              stroke={SERIES.primary}
              strokeWidth={2.5}
              fill="url(#gradientSales)"
              dot={{ r: 3, fill: SERIES.primary }}
              activeDot={{ r: 5 }}
            />
            {/* Daily Budget — dashed reference line */}
            <Area
              type="monotone"
              dataKey="dailyBudget"
              name="Daily Budget"
              stroke={SERIES.budget}
              strokeWidth={2}
              strokeDasharray="5 5"
              fill="none"
              dot={false}
            />
            {/* Prior Year Daily Sales — dotted comparison line */}
            <Area
              type="monotone"
              dataKey="pyDailySales"
              name="PY Daily Sales"
              stroke={SERIES.priorYearLine}
              strokeWidth={1.5}
              strokeDasharray="2 3"
              fill="none"
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
